import { WAIT_MAX_MINUTES, WAIT_UNIT_MINUTES, WAIT_UNITS, type PlanKey, type WaitUnit } from "@sagot/constants";
import type { StepDuration, WaitStep } from "../types";

const LONGEST_CAP: number = Math.max(...Object.values(WAIT_MAX_MINUTES));
const DEFAULT_WAIT: StepDuration = { value: 1, unit: "hours" };

/** The wait in minutes. */
export const waitMinutes = (duration: StepDuration): number => duration.value * WAIT_UNIT_MINUTES[duration.unit];

/** The longest wait a plan allows; while the plan is unknown the server alone decides (the longest cap applies here). */
export function waitCapMinutes(plan: string | undefined): number {
  return (plan ? WAIT_MAX_MINUTES[plan as PlanKey] : undefined) ?? LONGEST_CAP;
}

/** The cap written in the largest unit that gives a whole number above one ("24 hours", "30 days"). */
export function capLabel(minutes: number): { value: number; unit: WaitUnit } {
  const unit = [...WAIT_UNITS].reverse().find((candidate) => minutes % WAIT_UNIT_MINUTES[candidate] === 0 && minutes / WAIT_UNIT_MINUTES[candidate] > 1) ?? "minutes";
  return { value: minutes / WAIT_UNIT_MINUTES[unit], unit };
}

/** The largest value one unit accepts under the cap. */
export const maxForUnit = (unit: WaitUnit, plan: string | undefined): number => Math.max(Math.floor(waitCapMinutes(plan) / WAIT_UNIT_MINUTES[unit]), 1);

/** `true` when the wait is longer than the plan allows (the server refuses to publish it). */
export const waitExceedsPlan = (duration: StepDuration, plan: string | undefined): boolean => waitMinutes(duration) > waitCapMinutes(plan);

/** A wait lasts for a set time (optionally then until the office is open) or until the office opens. */
export const WAIT_MODES = ["for", "office"] as const;
export type WaitMode = (typeof WAIT_MODES)[number];

/** A wait with no length of its own lasts until the office opens. */
export const waitModeOf = (step: Pick<WaitStep, "duration">): WaitMode => (step.duration ? "for" : "office");

/**
 * The wait in the other mode: "until the office opens" has no length and always listens for the office; a set time comes back with a
 * length (the one it had, else an hour) and without the office, which is a choice of its own there.
 */
export function withWaitMode(step: WaitStep, mode: WaitMode): WaitStep {
  if (mode === "office") return { ...step, duration: undefined, until_office_hours: true };
  return { ...step, duration: step.duration ?? DEFAULT_WAIT, until_office_hours: undefined };
}
